import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  alpha,
  Button,
  Grid,
  Switch,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TabContext, TabPanel } from '@mui/lab';
import championshipLogo from 'assets/images/championship-logo.png';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React from 'react';

import { getTermsSections } from './services';

const useStyles = makeStyles((theme: any) => ({
  root: {
    flexGrow: 1,
    display: 'flex',
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    width: '100%',
    '& .MuiTab-root': {
      maxWidth: '100%',
      width: '100%',
      textAlign: 'left',
      alignItems: 'flex-start',
    },
  },
  nextButton: {
    backgroundColor: theme.palette.warning.main,
  },
  nextButton2: {
    backgroundColor: theme.palette.button.default,
  },
  title: {
    color: theme.accent.main,
  },
  centerContainer: {
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
  },
  border: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  paddingSubtitle: {
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  hover: {
    '&$hover:hover': {
      background: alpha(theme.palette.common.black, 0.5),
      color: theme.palette.secondary.main,
    },
  },
  nextbuttonfontL: {
    fontWeight: 500,
    color: 'black',
  },
  sidebar: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingLeft: 24,
  },
  contentPanel: {
    height: '100vh',
    overflowY: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
  },
}));

type OnboardingTermsAndConditionsSceneProps = {
  t: TFunction;
  theme: any;
  tabIndex: string;
  handleTabChange: (event: React.ChangeEvent<{}>, value: any) => void;
  a11yProps: (index: number) => {
    id: string;
    'aria-controls': string;
  };
  userAgreed: boolean;
  handleUserAgreed: () => void;
  handleAgreement: () => void;
  hideActions?: boolean;
};

const OnboardingTermsAndConditionsScene = ({
  t,
  theme,
  tabIndex,
  handleTabChange,
  a11yProps,
  userAgreed,
  handleUserAgreed,
  handleAgreement,
  hideActions = false,
}: OnboardingTermsAndConditionsSceneProps) => {
  const classes = useStyles();
  const SM = useMediaQuery(`(max-width:${theme.spacing(75)})`);
  const MD = useMediaQuery(`(max-width:${theme.spacing(120)})`);

  const termsSections = getTermsSections(t);
  const sectionEntries = Object.entries(termsSections);

  return (
    <Grid
      container
      style={{
        color: theme.palette.common.white,
        flexWrap: 'nowrap',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <TabContext value={tabIndex}>
        {!MD && (
          <Grid
            item
            style={{
              width: '25%',
              minWidth: '25%',
              maxWidth: '25%',
            }}
          >
            <div className={classes.sidebar}>
              {/* Logo */}
              <div
                style={{
                  flexShrink: 0,
                  marginTop: theme.spacing(2),
                }}
              >
                <img
                  src={championshipLogo}
                  alt="logo"
                  style={{ width: theme.spacing(20) }}
                />
              </div>

              <div
                style={{
                  flexShrink: 0,
                  paddingTop: theme.spacing(2),
                  paddingBottom: theme.spacing(1),
                }}
              >
                <Typography
                  variant="h4"
                  className={classes.title}
                  style={{ textAlign: 'left' }}
                >
                  {t('onboarding:terms-of-service')}
                </Typography>
              </div>

              <div
                style={{
                  flexShrink: 0,
                  paddingBottom: theme.spacing(4),
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ textAlign: 'left' }}
                >
                  {t('onboarding:read-terms-of-service')}
                </Typography>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                }}
              >
                <Tabs
                  data-testid="tabs-terms-and-condition"
                  orientation="vertical"
                  variant="scrollable"
                  scrollButtons="auto"
                  value={tabIndex}
                  onChange={handleTabChange}
                  className={classes.tabs}
                >
                  {sectionEntries.map(([sectionKey, { tabLabel }], index) => (
                    <Tab
                      key={sectionKey}
                      className={classes.hover}
                      value={sectionKey}
                      label={tabLabel}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
              </div>
            </div>
          </Grid>
        )}

        <Grid
          item
          style={
            MD
              ? { width: '100%' }
              : { width: '75%', minWidth: '75%', maxWidth: '75%' }
          }
        >
          {MD && (
            <div style={{ textAlign: 'center', padding: theme.spacing(3) }}>
              <img
                src={championshipLogo}
                alt="logo"
                style={{
                  width: theme.spacing(20),
                  marginBottom: theme.spacing(2),
                }}
              />
              <Typography variant="h4" className={classes.title}>
                {t('onboarding:terms-of-service')}
              </Typography>
              <Typography
                variant="subtitle2"
                style={{ marginTop: theme.spacing(1) }}
              >
                {t('onboarding:read-terms-of-service')}
              </Typography>
            </div>
          )}

          <div
            className={clsx({ [classes.contentPanel]: !MD })}
            style={{
              paddingRight: theme.spacing(4),
              paddingLeft: theme.spacing(3),
              ...(MD ? {} : { paddingTop: theme.spacing(4) }),
            }}
          >
            {MD ? (
              <>
                {sectionEntries.map(
                  ([sectionKey, { component: SectionComponent }]) => (
                    <SectionComponent key={sectionKey} />
                  ),
                )}
              </>
            ) : (
              <>
                {sectionEntries.map(
                  ([sectionKey, { component: SectionComponent }]) => (
                    <TabPanel key={sectionKey} value={sectionKey}>
                      <SectionComponent />
                    </TabPanel>
                  ),
                )}
              </>
            )}

            {!hideActions && (
              <Grid
                container
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: SM ? 0 : theme.spacing(3),
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(4),
                }}
              >
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <Switch
                    checked={userAgreed}
                    onChange={handleUserAgreed}
                    name="agreement"
                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                  />
                  <Typography variant="caption">
                    {t('onboarding:agree-terms-of-service')}
                  </Typography>
                </Grid>

                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingTop: 24,
                  }}
                >
                  <Typography variant="caption" style={{ marginRight: theme.spacing(2) }}>
                    {t('onboarding:proceed-disclaimer')}
                  </Typography>
                  <Button
                    disabled={!userAgreed}
                    style={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                    }}
                    color="secondary"
                    onClick={handleAgreement}
                    variant="contained"
                    classes={{ disabled: classes.nextButton }}
                  >
                    <Typography
                      variant="body2"
                      className={classes.nextbuttonfontL}
                    >
                      {t('onboarding:proceed')}
                    </Typography>
                    <FontAwesomeIcon
                      icon="check"
                      size="1x"
                      style={{ marginLeft: theme.spacing(1) }}
                      className={classes.nextbuttonfontL}
                    />
                  </Button>
                </Grid>
              </Grid>
            )}
          </div>
        </Grid>
      </TabContext>
    </Grid>
  );
};

export default OnboardingTermsAndConditionsScene;
